/**
 * V2 User Service - Direct Integration
 * Replaces Firebase Functions with direct Firestore access
 */

import { createQuery } from "@tanstack/solid-query";
import { queryKeys } from "@core/query";
import { userService, bookingService } from "@core/services-v2";
import type { User, Booking } from "@core/services-v2";
import type { UserDetail, UserSummary, UserStats } from "./types";
import type { BookingSummary } from "@modules/bookings/types";

const mapUserToSummary = (user: User): UserSummary => ({
  id: user.uid,
  displayName: user.displayName || user.email || user.phoneNumber || user.uid,
  email: user.email || null,
  phoneNumber: user.phoneNumber || null,
  createdAt: user.createdAt,
  lastLoginAt: user.lastLoginAt || user.lastSeenAt || null,
  stats: user.stats ? {
    totalCleanings: user.stats.totalBookings || null,
    totalSquareFootCleaned: user.stats.averageRating || null,
  } : null,
});

const mapBookingToSummary = (booking: Booking): BookingSummary => ({
  id: booking.id!,
  userId: booking.userId,
  status: booking.status,
  serviceId: booking.serviceId,
  roomType: booking.roomType,
  amount: booking.amount,
  currency: booking.currency,
  paymentIntentId: booking.paymentIntentId,
  source: booking.metadata?.source || null,
  createdAt: booking.createdAt,
  updatedAt: booking.updatedAt,
  schedule: {
    scheduledDate: booking.schedule?.scheduledDate || null,
    timeSlotLabel: booking.schedule?.timeSlotLabel || null,
    buildingId: booking.schedule?.buildingId || null,
    floor: booking.schedule?.floor || null,
    apartmentId: booking.schedule?.apartmentId || null,
    notes: booking.schedule?.notes || null,
  },
});

export const fetchUsers = async (take = 40): Promise<UserSummary[]> => {
  try {
    const users = await userService.getAllUsers(take);
    return users
      .map(mapUserToSummary)
      .sort((a, b) => {
        const left = (a.displayName ?? a.email ?? a.id).toLowerCase();
        const right = (b.displayName ?? b.email ?? b.id).toLowerCase();
        return left.localeCompare(right);
      });
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const fetchUserDetail = async (userId: string): Promise<UserDetail | null> => {
  try {
    const user = await userService.getUser(userId);
    if (!user) return null;

    const userSummary = mapUserToSummary(user);

    // Fetch recent bookings for this user
    const bookings = await bookingService.getBookingsByUser(userId);
    const recentBookings = bookings
      .slice(0, 5)
      .map(mapBookingToSummary);

    return {
      user: userSummary,
      recentBookings,
    };
  } catch (error) {
    console.error('Error fetching user detail:', error);
    throw error;
  }
};

export const getUserSummaryCached = async (userId: string): Promise<UserSummary | null> => {
  try {
    const user = await userService.getUser(userId);
    return user ? mapUserToSummary(user) : null;
  } catch (error) {
    console.error('Error fetching user summary:', error);
    return null;
  }
};

export const useUserListQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.users.root(),
    queryFn: () => fetchUsers(),
  }));

export const useUserDetailQuery = (userId: () => string | null) =>
  createQuery(() => {
    const id = userId();
    return {
      queryKey: queryKeys.users.byId(id ?? "detail"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchUserDetail(id) : Promise.resolve(null)),
    };
  });

export const useUserSummaryQuery = (userId: () => string | null) =>
  createQuery(() => {
    const id = userId();
    return {
      queryKey: queryKeys.users.summary(id ?? "summary"),
      enabled: Boolean(id),
      queryFn: () => (id ? getUserSummaryCached(id) : Promise.resolve(null)),
    };
  });

/**
 * Generic function to update a single user field
 *
 * @param userId - The user's ID
 * @param fieldName - The field name to update (must be a valid UserProfile key)
 * @param value - The new value for the field
 *
 * @example
 * await updateUserField(userId, 'displayName', 'John Doe');
 * await updateUserField(userId, 'email', 'john@example.com');
 */
export async function updateUserField(
  userId: string,
  fieldName: keyof Pick<User, 'displayName' | 'email' | 'phoneNumber' | 'photoURL'>,
  value: string
): Promise<void> {
  try {
    // Validate the field name (only allow editable fields)
    const allowedFields = ['displayName', 'email', 'phoneNumber', 'photoURL'];
    if (!allowedFields.includes(fieldName)) {
      throw new Error(`Field '${fieldName}' is not editable`);
    }

    // Update the user in Firestore
    await userService.updateUser(userId, { [fieldName]: value });

    console.log(`Successfully updated ${fieldName} for user ${userId}`);
  } catch (error) {
    console.error(`Error updating user field ${fieldName}:`, error);
    throw error;
  }
}